'use client'

import { useEffect, useState } from 'react'
import NextImage from 'next/image'
import { usePathname } from 'next/navigation'
import { X } from 'lucide-react'
import { useIsMobile } from '@/shared/hooks/use-mobile'
import { useAuth } from '@/features/auth'
import { routes } from '@/shared/routes'
import { detectPlatform, type WebPlatform } from '@/shared/app-invite/platform'
import { HUBHIVE_APP } from '@/shared/store-apps.constants'
import { isLoopSiteHost } from '@/shared/loop-site'

const DISMISS_KEY = 'hubhive-app-banner-dismissed'

/**
 * Where "Get" sends the phone: its own store listing. The bar is gated on a
 * phone-width viewport, not on OS, so a narrow desktop window reads as
 * 'desktop' here and gets the App Store listing — a real listing, rather than
 * a link back to the page it is already on.
 */
function storeUrl(platform: WebPlatform): string {
  return platform === 'android' ? HUBHIVE_APP.playStoreUrl : HUBHIVE_APP.appStoreUrl
}

export function SmartAppBanner() {
  const isMobile = useIsMobile()
  const pathname = usePathname()
  const { user } = useAuth()
  const [dismissed, setDismissed] = useState(true)
  // OS is client-only; resolve it after mount, as `AppDownloadCTA` does.
  const [platform, setPlatform] = useState<WebPlatform>('desktop')
  // The Loop site serves that page at `/` (a proxy rewrite), so there the
  // pathname says nothing and the host says everything. Client-only too.
  const [onLoopSite, setOnLoopSite] = useState(false)

  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1')
    setPlatform(detectPlatform())
    setOnLoopSite(isLoopSiteHost(window.location.host))
  }, [])

  // `/loop` IS a download page: its whole body is the store CTA for HubHive
  // Loop, and the phone reading it just scanned the Loop QR. A second "Get"
  // bar at the top, for a different app, would only compete with that. The
  // same page on the Loop site (`loop.hubhive.com/`) is the printed QR's
  // landing itself.
  const onDownloadPage = pathname === routes.loop || onLoopSite

  const visible = !user && isMobile && !dismissed && !onDownloadPage

  useEffect(() => {
    if (visible) {
      document.body.classList.add('has-app-banner')
    } else {
      document.body.classList.remove('has-app-banner')
    }
    return () => document.body.classList.remove('has-app-banner')
  }, [visible])

  if (!visible) return null

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1')
    setDismissed(true)
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-14 bg-card border-b border-border flex items-center gap-3 px-3">
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="p-1 -ml-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
      <NextImage
        src="/assets/hubhive-icon.png"
        alt="HubHive"
        width={36}
        height={36}
        loading="lazy"
        className="h-9 w-9 object-contain shrink-0"
      />
      <div className="flex-1" />
      <a
        href={storeUrl(platform)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary font-semibold text-sm uppercase tracking-wide px-4 py-1.5 rounded-full bg-primary/10 hover:bg-primary/20"
      >
        Get
      </a>
    </div>
  )
}
